import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import { API_BASE_URL } from '../config';
import SessionManager from '../utils/sessionManager';

const SuperAdminVerificationDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine active view from URL path
  const getActiveView = () => {
    if (location.pathname.includes('/candidates')) return 'candidates';
    if (location.pathname.includes('/settings')) return 'settings';
    return 'overview';
  };
  
  const [activeView, setActiveView] = useState(getActiveView());
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [config, setConfig] = useState(null);
  const [editingConfig, setEditingConfig] = useState(false);
  const [configForm, setConfigForm] = useState({
    voice_questions_count: 12,
    mcq_questions_count: 5,
    situational_questions_count: 3
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateDetails, setCandidateDetails] = useState(null);
  const [candidatesList, setCandidatesList] = useState([]);
  const [statusFilter, setStatusFilter] = useState('completed');
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [verificationDropdownOpen, setVerificationDropdownOpen] = useState(true);
  const session = SessionManager.getCurrentSession();

  useEffect(() => {
    // Update active view when location changes
    setActiveView(getActiveView());
  }, [location.pathname]);

  useEffect(() => {
    if (activeView === 'overview' || activeView === 'settings') {
      fetchData();
    }
    if (activeView === 'candidates' || activeView === 'overview') {
      fetchCandidatesList();
    }
  }, [activeView]);

  useEffect(() => {
    if (activeView === 'candidates') {
      fetchCandidatesList();
    }
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Fetch analytics
      const analyticsResponse = await fetch(`${API_BASE_URL}/verification/analytics/overview`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (analyticsResponse.ok) {
        const analyticsData = await analyticsResponse.json();
        setAnalytics(analyticsData);
      }

      // Fetch config
      const configResponse = await fetch(`${API_BASE_URL}/verification/config`);
      if (configResponse.ok) {
        const configData = await configResponse.json();
        setConfig(configData);
        setConfigForm({
          voice_questions_count: configData.voice_questions_count,
          mcq_questions_count: configData.mcq_questions_count,
          situational_questions_count: configData.situational_questions_count
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidatesList = async () => {
    try {
      setCandidatesLoading(true);
      const token = localStorage.getItem('token');
      
      let url = `${API_BASE_URL}/verification/candidates/list?limit=100`;
      if (statusFilter) {
        url += `&status_filter=${statusFilter}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCandidatesList(data.candidates || []);
      }
    } catch (error) {
      console.error('Error fetching candidates list:', error);
    } finally {
      setCandidatesLoading(false);
    }
  };

  const handleUpdateConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/verification/config?voice_questions_count=${configForm.voice_questions_count}&mcq_questions_count=${configForm.mcq_questions_count}&situational_questions_count=${configForm.situational_questions_count}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        alert('Configuration updated successfully!');
        setEditingConfig(false);
        fetchData();
      } else {
        alert('Failed to update configuration');
      }
    } catch (error) {
      console.error('Error updating config:', error);
      alert('Error updating configuration');
    }
  };

  const handleSearchCandidate = async () => {
    if (!searchQuery.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/verification/candidate/${searchQuery}/details`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCandidateDetails(data);
        setSelectedCandidate(searchQuery);
      } else {
        alert('Candidate not found or not verified');
        setCandidateDetails(null);
      }
    } catch (error) {
      console.error('Error fetching candidate details:', error);
      alert('Error fetching candidate details');
    }
  };

  const handleSelectCandidate = async (candidateId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/verification/candidate/${candidateId}/details`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCandidateDetails(data);
        setSelectedCandidate(candidateId);
        setSearchQuery(candidateId);
      } else {
        alert('Failed to load candidate details');
      }
    } catch (error) {
      console.error('Error fetching candidate details:', error);
      alert('Error fetching candidate details');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const totalQuestions = config ? 
    config.voice_questions_count + config.mcq_questions_count + config.situational_questions_count : 20;

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Sidebar Navigation */}
      <div className="w-72 bg-white shadow-lg min-h-screen sticky top-0">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
              <Icon name="admin_panel_settings" size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Admin Panel</h2>
              <p className="text-xs text-gray-500">Super Admin</p>
            </div>
          </div>
        </div>

        <nav className="p-4 space-y-1">
          {/* Overview */}
          <button
            onClick={() => navigate('/super-admin/dashboard')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-all"
          >
            <Icon name="dashboard" size={20} />
            <span>Overview</span>
          </button>

          {/* Management Section */}
          <div className="pt-4 pb-2">
            <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Management</p>
          </div>
          
          <button
            onClick={() => navigate('/super-admin/dashboard')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-all"
          >
            <Icon name="work" size={20} />
            <span>HR Companies</span>
          </button>

          <button
            onClick={() => navigate('/super-admin/dashboard')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-all"
          >
            <Icon name="school" size={20} />
            <span>Course Providers</span>
          </button>

          <button
            onClick={() => navigate('/super-admin/dashboard')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-all"
          >
            <Icon name="cloud" size={20} />
            <span>SaaS Providers</span>
          </button>

          {/* API & Integration */}
          <div className="pt-4 pb-2">
            <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">API & Integration</p>
          </div>

          <button
            onClick={() => navigate('/super-admin/dashboard')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-all"
          >
            <Icon name="key" size={20} />
            <span>API Keys</span>
          </button>

          {/* Financial */}
          <div className="pt-4 pb-2">
            <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Financial</p>
          </div>

          <button
            onClick={() => navigate('/super-admin/dashboard')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-all"
          >
            <Icon name="receipt_long" size={20} />
            <span>Transactions</span>
          </button>

          <button
            onClick={() => navigate('/super-admin/dashboard')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-all"
          >
            <Icon name="analytics" size={20} />
            <span>Token Usage</span>
          </button>

          <button
            onClick={() => navigate('/super-admin/dashboard')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-all"
          >
            <Icon name="account_balance_wallet" size={20} />
            <span>Credits</span>
          </button>

          <button
            onClick={() => navigate('/super-admin/payments')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-all"
          >
            <Icon name="payments" size={20} />
            <span>Payments</span>
          </button>

          {/* System */}
          <div className="pt-4 pb-2">
            <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">System</p>
          </div>

          {/* Verification with Dropdown */}
          <div className="relative">
            <button
              onClick={() => setVerificationDropdownOpen(!verificationDropdownOpen)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-blue-50 text-blue-700 font-semibold transition-all"
            >
              <div className="flex items-center gap-3">
                <Icon name="verified_user" size={20} />
                <span>Verification</span>
              </div>
              <Icon 
                name={verificationDropdownOpen ? "expand_less" : "expand_more"} 
                size={20}
              />
            </button>
            
            {verificationDropdownOpen && (
              <div className="ml-8 mt-1 space-y-1 border-l-2 border-gray-200 pl-2">
                <button
                  onClick={() => navigate('/super-admin/verification')}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
                    activeView === 'overview'
                      ? 'bg-blue-50 text-blue-700 font-semibold'
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-700'
                  }`}
                >
                  <Icon name="dashboard" size={18} />
                  <span>Score Dashboard</span>
                </button>
                <button
                  onClick={() => navigate('/super-admin/verification/candidates')}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
                    activeView === 'candidates'
                      ? 'bg-blue-50 text-blue-700 font-semibold'
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-700'
                  }`}
                >
                  <Icon name="person_search" size={18} />
                  <span>Candidate Search</span>
                </button>
                <button
                  onClick={() => navigate('/super-admin/verification/settings')}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
                    activeView === 'settings'
                      ? 'bg-blue-50 text-blue-700 font-semibold'
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-700'
                  }`}
                >
                  <Icon name="settings" size={18} />
                  <span>Settings</span>
                </button>
              </div>
            )}
          </div>

          <button
            onClick={() => navigate('/super-admin/dashboard')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-all"
          >
            <Icon name="people" size={20} />
            <span>Demo Leads</span>
          </button>

          <button
            onClick={() => navigate('/super-admin/dashboard')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-all"
          >
            <Icon name="phone" size={20} />
            <span>Phone Test</span>
          </button>
        </nav>

        {/* User Info at Bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">
                {session?.username?.charAt(0).toUpperCase() || 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{session?.username || 'aptirix'}</p>
              <p className="text-xs text-gray-500">Super Admin</p>
            </div>
            <button
              onClick={() => {
                SessionManager.clearSession();
                navigate('/');
              }}
              className="p-2 text-gray-400 hover:text-gray-600 transition"
            >
              <Icon name="logout" size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                <Icon name="verified_user" size={32} className="text-blue-600" />
                Profile Verification Dashboard
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                {activeView === 'overview' && 'View verification analytics and metrics'}
                {activeView === 'candidates' && 'Search and view candidate verification details'}
                {activeView === 'settings' && 'Configure verification assessment settings'}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/super-admin/dashboard')}
              className="flex items-center gap-2"
            >
              <Icon name="arrow_back" size={20} />
              Back to Dashboard
            </Button>
          </div>

          {/* Navigation Tabs */}
          <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => navigate('/super-admin/verification')}
              className={`px-6 py-3 font-medium transition-all ${
                activeView === 'overview'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
              }`}
            >
              <Icon name="dashboard" size={20} className="inline mr-2" />
              Score Dashboard
            </button>
            <button
              onClick={() => navigate('/super-admin/verification/candidates')}
              className={`px-6 py-3 font-medium transition-all ${
                activeView === 'candidates'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
              }`}
            >
              <Icon name="person_search" size={20} className="inline mr-2" />
              Candidate Search
            </button>
            <button
              onClick={() => navigate('/super-admin/verification/settings')}
              className={`px-6 py-3 font-medium transition-all ${
                activeView === 'settings'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
              }`}
            >
              <Icon name="settings" size={20} className="inline mr-2" />
              Settings
            </button>
          </div>
        </div>

        {/* Overview View - Analytics */}
        {activeView === 'overview' && analytics && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <Icon name="people" size={32} className="text-blue-600" />
                <span className="text-3xl font-bold text-gray-900 dark:text-white">
                  {analytics.total_verifications}
                </span>
              </div>
              <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                Total Verifications
              </h3>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <Icon name="check_circle" size={32} className="text-green-600" />
                <span className="text-3xl font-bold text-gray-900 dark:text-white">
                  {analytics.completed_count}
                </span>
              </div>
              <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                Completed
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {analytics.completion_rate.toFixed(1)}% completion rate
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <Icon name="star" size={32} className="text-yellow-600" />
                <span className="text-3xl font-bold text-gray-900 dark:text-white">
                  {analytics.average_score.toFixed(1)}
                </span>
              </div>
              <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                Average Score
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Out of 100
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <Icon name="psychology" size={32} className="text-purple-600" />
                <span className="text-3xl font-bold text-gray-900 dark:text-white">
                  {analytics.average_skill_score.toFixed(1)}
                </span>
              </div>
              <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                Avg Skill Score
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Technical proficiency
              </p>
            </div>
          </div>
          </div>
        )}

        {/* Settings View - Configuration Only */}
        {activeView === 'settings' && (
          <div className="max-w-2xl">
            {/* Configuration Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Icon name="settings" size={24} className="text-blue-600" />
                  Assessment Configuration
                </h2>
                {!editingConfig && (
                  <Button
                    size="sm"
                    onClick={() => setEditingConfig(true)}
                    className="flex items-center gap-2"
                  >
                    <Icon name="edit" size={16} />
                    Edit
                  </Button>
                )}
              </div>

              {editingConfig ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Voice Questions
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="30"
                      value={configForm.voice_questions_count}
                      onChange={(e) => setConfigForm({...configForm, voice_questions_count: parseInt(e.target.value)})}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Multiple Choice Questions
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={configForm.mcq_questions_count}
                      onChange={(e) => setConfigForm({...configForm, mcq_questions_count: parseInt(e.target.value)})}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Situational Questions
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={configForm.situational_questions_count}
                      onChange={(e) => setConfigForm({...configForm, situational_questions_count: parseInt(e.target.value)})}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <strong>Total Questions:</strong> {configForm.voice_questions_count + configForm.mcq_questions_count + configForm.situational_questions_count}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Estimated time: {Math.ceil((configForm.voice_questions_count + configForm.mcq_questions_count + configForm.situational_questions_count) * 1.2)} minutes
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      onClick={handleUpdateConfig}
                      className="flex-1"
                    >
                      Save Changes
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingConfig(false);
                        setConfigForm({
                          voice_questions_count: config.voice_questions_count,
                          mcq_questions_count: config.mcq_questions_count,
                          situational_questions_count: config.situational_questions_count
                        });
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Icon name="mic" size={24} className="text-blue-600" />
                      <span className="font-medium text-gray-900 dark:text-white">Voice Questions</span>
                    </div>
                    <span className="text-2xl font-bold text-blue-600">
                      {config?.voice_questions_count}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Icon name="quiz" size={24} className="text-green-600" />
                      <span className="font-medium text-gray-900 dark:text-white">Multiple Choice</span>
                    </div>
                    <span className="text-2xl font-bold text-green-600">
                      {config?.mcq_questions_count}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Icon name="psychology" size={24} className="text-purple-600" />
                      <span className="font-medium text-gray-900 dark:text-white">Situational</span>
                    </div>
                    <span className="text-2xl font-bold text-purple-600">
                      {config?.situational_questions_count}
                    </span>
                  </div>

                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <strong>Total:</strong> {totalQuestions} questions
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Time per question: {config?.time_per_question_seconds}s • Thinking time: {config?.thinking_time_seconds}s
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Skill Uniqueness Distribution */}
            {analytics && analytics.skill_uniqueness_distribution && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                  <Icon name="insights" size={24} className="text-purple-600" />
                  Skill Uniqueness Distribution
                </h2>
                <div className="space-y-4">
                  {Object.entries(analytics.skill_uniqueness_distribution).map(([level, count]) => (
                    <div key={level}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                          {level}
                        </span>
                        <span className="text-sm font-bold text-gray-900 dark:text-white">
                          {count}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            level === 'unique' ? 'bg-purple-600' :
                            level === 'rare' ? 'bg-blue-600' :
                            level === 'intermediate' ? 'bg-green-600' : 'bg-gray-400'
                          }`}
                          style={{ width: `${(count / analytics.completed_count) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Candidates View - Search & List Only */}
        {activeView === 'candidates' && (
          <div>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                <Icon name="search" size={24} className="text-blue-600" />
                Candidate Verification Details
              </h2>

              {/* Search */}
              <div className="mb-6">
                <div className="flex gap-3">
                  <input
                    type="text"
                    placeholder="Enter candidate ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchCandidate()}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                  <Button onClick={handleSearchCandidate}>
                    Search
                  </Button>
                </div>
              </div>

              {/* Candidate Details */}
              {candidateDetails ? (
                <div className="space-y-6">
                  {/* Header */}
                  <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {candidateDetails.candidate_name}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        candidateDetails.status === 'completed' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                      }`}>
                        {candidateDetails.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Verification ID: {candidateDetails.verification_id}
                    </p>
                  </div>

                  {/* Overall Score */}
                  <div className="text-center p-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl">
                    <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">
                      Overall Score
                    </p>
                    <p className="text-5xl font-bold text-blue-600 mb-2">
                      {candidateDetails.overall_score?.toFixed(1)}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Out of 100
                    </p>
                  </div>

                  {/* Segment Scores */}
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
                      Segment Scores
                    </h4>
                    <div className="space-y-3">
                      {[
                        { label: 'Technical Skills', score: candidateDetails.skill_score, icon: 'code' },
                        { label: 'Project Experience', score: candidateDetails.project_score, icon: 'work' },
                        { label: 'Work Experience', score: candidateDetails.experience_score, icon: 'business_center' },
                        { label: 'Problem Solving', score: candidateDetails.problem_solving_score, icon: 'psychology' },
                        { label: 'Communication', score: candidateDetails.communication_score, icon: 'chat' }
                      ].map((item) => (
                        <div key={item.label}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <Icon name={item.icon} size={16} className="text-gray-600 dark:text-gray-400" />
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {item.label}
                              </span>
                            </div>
                            <span className="text-sm font-bold text-gray-900 dark:text-white">
                              {item.score?.toFixed(1)}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${item.score}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Skill Uniqueness */}
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                      Skill Uniqueness
                    </h4>
                    <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <p className="text-sm font-semibold text-purple-600 dark:text-purple-400 capitalize mb-3">
                        Level: {candidateDetails.skill_uniqueness_level}
                      </p>
                      {candidateDetails.unique_skills && candidateDetails.unique_skills.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Unique Skills:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {candidateDetails.unique_skills.map((skill, idx) => (
                              <span key={idx} className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 text-xs rounded-full">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {candidateDetails.common_skills && candidateDetails.common_skills.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Common Skills:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {candidateDetails.common_skills.map((skill, idx) => (
                              <span key={idx} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-full">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Timestamps */}
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600 dark:text-gray-400">Started</p>
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {new Date(candidateDetails.started_at).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 dark:text-gray-400">Completed</p>
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {candidateDetails.completed_at ? new Date(candidateDetails.completed_at).toLocaleString() : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon name="search" size={48} className="text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    Search for a candidate to view their verification details
                  </p>
                </div>
              )}
            </div>

            {/* Candidates List */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Icon name="people" size={24} className="text-blue-600" />
                  Verified Candidates
                </h2>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                >
                  <option value="">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="in_progress">In Progress</option>
                </select>
              </div>

              {candidatesLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : candidatesList.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {candidatesList.map((candidate) => (
                    <div
                      key={candidate.candidate_id}
                      onClick={() => handleSelectCandidate(candidate.candidate_id)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                        selectedCandidate === candidate.candidate_id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-gray-900 dark:text-white">
                          {candidate.candidate_name}
                        </h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          candidate.status === 'completed'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}>
                          {candidate.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                        {candidate.candidate_email}
                      </p>
                      {candidate.overall_score && (
                        <div className="flex items-center gap-4 text-sm">
                          <div className="flex items-center gap-1">
                            <Icon name="star" size={16} className="text-yellow-500" />
                            <span className="font-semibold text-gray-900 dark:text-white">
                              {candidate.overall_score.toFixed(1)}
                            </span>
                          </div>
                          {candidate.skill_uniqueness_level && (
                            <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs rounded-full capitalize">
                              {candidate.skill_uniqueness_level}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Icon name="people" size={48} className="text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    No candidates found
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

export default SuperAdminVerificationDashboard;
